import styled from '@emotion/styled';
import { useEffect } from 'react';
import { ValuesType } from 'utility-types';
import { MenuButton } from '~/modules/Elements/Menu';
import events from '~/modules/GameEvents/GameEvents';
import { useEventListenerSelector } from '~/modules/GameEvents/hooks';
import PlayersManager from '~/modules/Players/PlayersManager';
import useKeyboardNav from '~/modules/hooks/useKeyboardNav';
import ConnectRemoteMic from '~/routes/ConnectRemoteMic/ConnectRemoteMic';
import InputSources from '~/routes/SelectInput/InputSources';
import MicCheck from '~/routes/SelectInput/MicCheck';
import usePlayerNumberPreset from '~/routes/SelectInput/hooks/usePlayerNumberPreset';
import { useRemoteMicAutoselect } from '~/routes/SelectInput/hooks/useRemoteMicAutoselect';
import { MicSetupPreference } from '~/routes/Settings/SettingsState';

interface Props {
  onSetupComplete: (complete: boolean) => void;
  onBack: () => void;
  onSave: () => void;
  closeButtonText: string;
  changePreference: (pref: ValuesType<typeof MicSetupPreference>) => void;
}

function RemoteMics(props: Props) {
  usePlayerNumberPreset(2);
  const { register } = useKeyboardNav({ onBackspace: props.onBack });

  useRemoteMicAutoselect();

  const players = useEventListenerSelector(
    [events.inputListChanged, events.playerInputChanged, events.playerRemoved],
    () => {
      return PlayersManager.getInputs()
        .map((input) => (input.source === 'Remote Microphone' ? input : null))
        .map((input) => (input ? InputSources.getInputForPlayerSelected(input) : null));
    },
  );

  const onContinue = () => {
    props.onSave();
  };

  const isComplete = !!players[0]?.label && !!players[1]?.label;

  useEffect(() => {
    props.onSetupComplete(isComplete);
  }, [isComplete]);

  return (
    <Wrap>
      <ConnectRemoteMic />

      <Helper>
        <h4>You can connect multiple phones in advance.</h4>
        <h4>You will be able to connect phones later.</h4>
      </Helper>

      <MicCheck />

      <Buttons>
        <MenuButton {...register('back-button', props.onBack)}>Change Input Type</MenuButton>
        <MenuButton {...register('save-button', onContinue, undefined, true)}>{props.closeButtonText}</MenuButton>
      </Buttons>
    </Wrap>
  );
}

const Helper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  h4 {
    line-height: 1.2;
  }

  @media (max-width: 520px) {
    h4 {
      font-size: 1.15rem;
    }
  }
`;

const Wrap = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;

  min-height: 0;
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: auto; /* ✅ empuja los botones abajo */
  padding-bottom: calc(env(safe-area-inset-bottom) + 8px);
`;

export default RemoteMics;
